import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import Button from 'components/button';
import {
  INVOICE_REMINDER_RUN_STARTED,
  REMINDER_LEVELS,
  countText,
  invoiceRemindersText,
} from 'components/invoiceReminders/invoiceRemindersText';
import ModalBody from 'components/modal/components/body';
import ModalFooter from 'components/modal/components/footer';
import Modalheader from 'components/modal/components/header';
import useModal from 'components/modal/useModal';
import UseContext from 'hooks/useContext';
import ApiDatabase from 'server';

// The largest page the api serves: "À faire" is counted in as few reads as possible.
const COUNT_PAGE_SIZE = 100;
// Its own key: counting never cancels the list read behind the modal.
const COUNT_ABORT_KEY = 'admin/invoices/reminders|remindAll';

/**
 * What "Tout relancer" would send, from every page of "À faire": the step due
 * of each invoice not suspended (the run skips suspended ones), the suspended
 * ones, and those whose steps are all used (no step due: nothing to send).
 */
export const countRemindAll = (items) => {
  const byLevel = Object.fromEntries(
    REMINDER_LEVELS.map((level) => [level, 0]),
  );
  let suspended = 0;
  let exhausted = 0;
  items.forEach((item) => {
    if (!item.dueStep) {
      exhausted += 1;
    } else if (item.suspended) {
      suspended += 1;
    } else if (
      Object.prototype.hasOwnProperty.call(byLevel, item.dueStep.level)
    ) {
      byLevel[item.dueStep.level] += 1;
    }
  });
  const total = Object.values(byLevel).reduce((sum, count) => sum + count, 0);
  return { byLevel, total, suspended, exhausted };
};

const readAllToDo = (onDone, onFailed) => {
  const items = [];
  const readPage = (page) => {
    ApiDatabase.getInvoiceReminders(
      { tab: 'todo', page, limit: COUNT_PAGE_SIZE },
      (data) => {
        items.push(...(data?.items ?? []));
        if (page < (data?.pages ?? 0)) readPage(page + 1);
        else onDone(items);
      },
      (error) => {
        if (error?.name === 'CanceledError') return;
        onFailed();
      },
      { abortKey: COUNT_ABORT_KEY },
    );
  };
  readPage(1);
};

/**
 * The confirmation of "Tout relancer" (JBWEB-1283): how many invoices of "À
 * faire" each step would send, then `POST admin/invoices/reminders/runs`
 * without a list. The run goes on in the background: INVOICE_REMINDER_RUN_STARTED
 * hands its `runId` to the page, which follows it, and so does a 409 with the
 * run already going.
 */
const ModalInvoiceRemindAll = () => {
  const { toggle } = useModal();
  const { language, setModalDataReturn } = UseContext();
  const text = invoiceRemindersText(language);
  const [counts, setCounts] = useState(null);
  const [countFailed, setCountFailed] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [failure, setFailure] = useState(null);
  const isMounted = useRef(true);

  const count = useCallback(() => {
    setCounts(null);
    setCountFailed(false);
    readAllToDo(
      (items) => {
        if (isMounted.current) setCounts(countRemindAll(items));
      },
      () => {
        if (isMounted.current) setCountFailed(true);
      },
    );
  }, []);

  useEffect(() => {
    isMounted.current = true;
    count();
    return () => {
      isMounted.current = false;
      ApiDatabase.abortRequest?.(COUNT_ABORT_KEY);
    };
  }, [count]);

  const follow = (runId) => {
    setModalDataReturn({ type: INVOICE_REMINDER_RUN_STARTED, runId });
    toggle();
  };

  const confirm = () => {
    if (isStarting) return;
    setIsStarting(true);
    setFailure(null);
    ApiDatabase.postInvoiceReminderRun(
      {},
      (data) => {
        setIsStarting(false);
        if (data?.runId) {
          follow(data.runId);
          return;
        }
        setFailure(text('remindAllModal.errors.generic'));
      },
      (error) => {
        if (error?.name === 'CanceledError') return;
        setIsStarting(false);
        // Never the error object in the console: axios keeps the request, token included.
        const body = error?.response?.data;
        if (body?.errorCode === 'REMINDER_RUN_ALREADY_RUNNING' && body.runId) {
          toast.info(text('run.alreadyRunning'));
          follow(body.runId);
          return;
        }
        const known = [
          'REMINDER_RUN_ALREADY_RUNNING',
          'BILLING_MAIL_DISABLED',
          'BILLING_MAIL_TEMPLATE_MISSING',
          'INVOICE_NUMBERS_INVALID',
        ];
        setFailure(
          text(
            `remindAllModal.errors.${known.includes(body?.errorCode) ? body.errorCode : 'generic'}`,
          ),
        );
      },
    );
  };

  let summary;
  if (countFailed) {
    summary = (
      <div
        role="alert"
        className="flex flex-col items-start gap-2 text-gray-700"
      >
        <div>{text('remindAllModal.loadFailed')}</div>
        <Button color="SECONDARY" onClick={count}>
          {text('remindAllModal.retryLoad')}
        </Button>
      </div>
    );
  } else if (!counts) {
    summary = (
      <div role="status" className="text-gray-500">
        {text('remindAllModal.loading')}
      </div>
    );
  } else if (counts.total === 0) {
    summary = (
      <p className="font-medium text-gray-900">
        {text('remindAllModal.nothing')}
      </p>
    );
  } else {
    summary = (
      <div className="flex flex-col gap-1">
        <p className="font-medium text-gray-900">
          {countText(text, 'remindAllModal.count', counts.total)}
        </p>
        <ul
          className="list-disc pl-5 text-gray-700"
          aria-label={text('remindAllModal.title')}
        >
          {REMINDER_LEVELS.filter((level) => counts.byLevel[level] > 0).map(
            (level) => (
              <li key={level}>
                {text('remindAllModal.step', {
                  label: text(`badges.${level}`),
                  count: counts.byLevel[level],
                })}
              </li>
            ),
          )}
        </ul>
      </div>
    );
  }

  const canConfirm = Boolean(counts) && counts.total > 0 && !isStarting;

  return (
    <>
      <Modalheader hide={toggle} hideShow={!isStarting}>
        {text('remindAllModal.title')}
      </Modalheader>
      <ModalBody>
        <div className="flex flex-col gap-3 text-sm">
          <p className="text-gray-700">{text('remindAllModal.intro')}</p>
          {summary}
          <div
            role="note"
            className="flex flex-col gap-1 rounded-lg bg-yellow-50 px-4 py-3 text-yellow-800"
          >
            <span>{text('remindAllModal.suspendedRule')}</span>
            {counts?.suspended > 0 && (
              <span>
                {countText(text, 'remindAllModal.suspended', counts.suspended)}
              </span>
            )}
            {counts?.exhausted > 0 && (
              <span>
                {countText(text, 'remindAllModal.exhausted', counts.exhausted)}
              </span>
            )}
            <span>{text('remindAllModal.triedRule')}</span>
          </div>
          {failure && (
            <div
              role="alert"
              className="rounded-lg bg-red-50 px-4 py-3 text-red-700"
            >
              {failure}
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          size="LG"
          color={isStarting ? 'DISABLED' : 'SECONDARY'}
          disabled={isStarting}
          onClick={toggle}
        >
          {text('remindAllModal.cancel')}
        </Button>
        <Button
          size="LG"
          color={canConfirm ? 'PRIMARY' : 'DISABLED'}
          disabled={!canConfirm}
          onClick={confirm}
        >
          {isStarting
            ? text('remindAllModal.starting')
            : text('remindAllModal.confirm')}
        </Button>
      </ModalFooter>
    </>
  );
};

export default ModalInvoiceRemindAll;
